/**
 * Weight & prioritize qualifications — drawer edition (STN client round
 * 2026-08). Replaces the drag-and-drop accordion with a per-qualification
 * 5-way segmented toggle (Required / High / Medium / Low / Off), opened from
 * the toolbar's "Adjust weighting" button.
 *
 * The ratified panel behaviors survive the re-skin:
 *   - escape hatch out of Required (7-13 finding 1) = pick any other segment;
 *   - recoverable removal (7-13) = Off is the `removed` area, toggling back
 *     out restores;
 *   - the server-echoed applied config is the single hydration source, and
 *     gating flags round-trip untouched (7-15 judgment-call items).
 * The old panel's inline TEXT editing did not carry over — the client mockup
 * shows none (flagged in the round's PR).
 *
 * All state logic stays in utils/qualification-weighting.ts (vitest-covered);
 * this component renders SegmentedDrafts and forwards events.
 */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import type { MapsQualification, MapsQualificationConfigEntry } from '../../models/maps.types';
import {
    AddItem,
    BuildDraftState,
    IsConfigDirty,
    SEGMENT_KEYS,
    SEGMENT_LABELS,
    SegmentedDrafts,
    SegmentSummary,
    SetSegment,
    ToConfigEntries,
    type QualificationDraftState,
    type SegmentedDraft,
    type SegmentKey,
} from '../../utils/qualification-weighting';

@Component({
    selector: 'maps-weighting-drawer',
    standalone: false,
    templateUrl: './weighting-drawer.component.html',
    styleUrl: './weighting-drawer.component.scss',
})
export class WeightingDrawerComponent implements OnInit {
    /** Server-echoed APPLIED configuration — the only hydration source. */
    @Input({ required: true }) qualifications: MapsQualification[] = [];
    @Input() isApplying = false;
    @Input() configApplied = false;
    /** Update results (rerun with the drafted config). */
    @Output() apply = new EventEmitter<MapsQualificationConfigEntry[]>();
    /** Restore original rankings (rerun without a config). */
    @Output() restore = new EventEmitter<void>();
    @Output() closed = new EventEmitter<void>();

    readonly segmentKeys = SEGMENT_KEYS;
    readonly segmentLabels = SEGMENT_LABELS;

    rows: SegmentedDraft[] = [];
    summary = { required: 0, preferred: 0, off: 0 };
    addText = '';

    private state!: QualificationDraftState;
    /** 'c'-suffixed keys sort after every server card (see SegmentedDrafts). */
    private customCounter = 0;

    ngOnInit(): void {
        this.state = BuildDraftState(this.qualifications);
        this.refresh();
    }

    get dirty(): boolean {
        return IsConfigDirty(this.state, this.qualifications);
    }

    /** Primary footer action: apply when dirty, restore when a config is active. */
    get primaryLabel(): string {
        if (this.isApplying) return 'Applying…';
        if (this.dirty) return 'Update results';
        return this.configApplied ? 'Restore original rankings' : 'Update results';
    }

    get primaryDisabled(): boolean {
        return this.isApplying || (!this.dirty && !this.configApplied);
    }

    OnSegment(row: SegmentedDraft, segment: SegmentKey): void {
        if (row.segment === segment) return;
        SetSegment(this.state, row.draft.localKey, segment);
        this.refresh();
    }

    /** Custom (never-applied) cards can be deleted outright; server-known
     *  cards use Off instead, staying recoverable. */
    CanDelete(row: SegmentedDraft): boolean {
        return row.draft.id === null;
    }

    DeleteCustom(row: SegmentedDraft): void {
        for (const list of [this.state.required, this.state.veryImportant, this.state.important, this.state.niceToHave, this.state.removed]) {
            const ix = list.findIndex((d) => d.localKey === row.draft.localKey);
            if (ix >= 0) {
                list.splice(ix, 1);
                break;
            }
        }
        this.refresh();
    }

    AddQualification(): void {
        // New cards land in Medium — the neutral middle of the scale.
        if (AddItem(this.state, this.addText, 'important', `qk-c${this.customCounter++}`)) {
            this.addText = '';
            this.refresh();
        }
    }

    OnPrimary(): void {
        if (this.primaryDisabled) return;
        if (this.dirty) {
            this.apply.emit(ToConfigEntries(this.state));
        } else {
            this.restore.emit();
        }
    }

    SegmentClass(segment: SegmentKey): string {
        return `seg seg--${segment}`;
    }

    TrackByRow(_ix: number, row: SegmentedDraft): string {
        return row.draft.localKey;
    }

    private refresh(): void {
        this.rows = SegmentedDrafts(this.state);
        this.summary = SegmentSummary(this.state);
    }
}
